// dependency-graph-ignore-file

import { client, getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api';
import { type BridgeData } from '@databases/database-state/state/bridge-data';
import { activityLogger } from '@databases/logs/activity-logger';
import { setAppMode, type AppMode } from '@databases/modes';
import { NativeEmbedPanelManager } from '@databases/panel/managers';
import { container } from '@databases/service-container';
import { captureException } from '@sentry/core';

import { maybeParseConfluenceDatabasesBridgeClientParams } from '@canvas/databases-bridge-api';
import { EnabledFeaturesCommand } from '@canvas/databases-bridge-api/commands';
import { mergeEnabledFeatures } from '@canvas/databases-bridge-api/utils';

import { mapBridgeCommandsToLocalMethods } from './map-bridge-commands-to-local-methods';
import { sendInitialBridgeCommands } from './send-initial-bridge-commands';
import { getLocalEnabledFeatureList } from './utils/get-local-enabled-feature-list';

const DEFAULT_HANDSHAKE_TIMEOUT_MS = 5000;

const withTimeout = <T>(value: PromiseLike<T> | T, ms: number) =>
  new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('Bridge handshake timeout')), ms);

    Promise.resolve(value)
      .then((v) => {
        clearTimeout(t);
        resolve(v);
      })
      .catch((e) => {
        clearTimeout(t);
        reject(e);
      });
  });

/**
 * When there is an issue establishing the bridge connection, fallback to EMBED mode.
 */
function defaultOnFallback(e?: any) {
  setAppMode('EMBED');

  captureException(
    e ?? new Error('Failed to establish Native Embed Bridge. Falling back to EMBED.')
  );
}

/**
 * Attempts to initialize a native embed bridge for communication between local and external clients.
 *
 * This function establishes a bridge connection in native embed mode by:
 * 1. Validating the app mode is NATIVE_EMBED
 * 2. Parsing bridge parameters from the URL
 * 3. Performing a handshake between local and external clients
 * 4. Merging feature lists from both clients
 * 5. Mapping bridge commands to local methods
 *
 * @param appMode - The current application mode, must be 'NATIVE_EMBED' to proceed
 * @param getBridgeData - Function that returns bridge data needed for initialization
 * @param options - Optional configuration object
 * @param options.onFallback - Callback function to execute when bridge initialization fails or falls back
 * @param options.handshakeTimeoutMs - Timeout in milliseconds for the handshake process
 *
 * @returns A cleanup function to destroy the bridge if initialization succeeds, otherwise undefined
 *
 * @throws Will call the onFallback function if any errors occur during initialization
 *
 * @example
 * ```typescript
 * const cleanup = await tryInitializeNativeEmbedBridge(
 *   'NATIVE_EMBED',
 *   () => currentBridgeData,
 *   {
 *     onFallback: (error) => console.error('Bridge init failed:', error),
 *     handshakeTimeoutMs: 5000
 *   }
 * );
 *
 * // Later, cleanup the bridge
 * cleanup?.();
 * ```
 */
export async function tryInitializeNativeEmbedBridge(
  appMode: AppMode,
  getBridgeData: () => BridgeData,
  options?: { onFallback?: (e?: any) => void; handshakeTimeoutMs?: number }
) {
  const onFallback = options?.onFallback ?? defaultOnFallback;

  /**
   * If the app is not in native embed mode, return. We don't support this bridge
   * in other modes.
   */
  if (appMode !== 'NATIVE_EMBED') {
    return;
  }

  const params = maybeParseConfluenceDatabasesBridgeClientParams(window.location.search);

  /**
   * If the bridge params have not been properly set or are omitted, there is no proper way
   * to establish communication between a local and external client, so we return.
   */
  if (!params) {
    // Fallback: unable to initialize native embed without bridge params
    onFallback();

    return;
  }

  try {
    const localClient = client(params.localClientId);
    const externalClient = client(params.externalClientId);
    const bridge = getBrowserBridgeAPI();
    const localFeatureList = getLocalEnabledFeatureList();

    bridge.ready(localClient);

    /**
     * To initiate a "handshake" with the external client, the local client
     * sends over a list of currently enabled features and waits for one in return.
     */
    const command = bridge.send(EnabledFeaturesCommand, {
      features: localFeatureList,
      senderId: localClient,
      receiverId: externalClient,
    });

    const externalFeaturesPromise = command.result();
    const externalFeatures = await withTimeout(
      externalFeaturesPromise,
      options?.handshakeTimeoutMs ?? DEFAULT_HANDSHAKE_TIMEOUT_MS
    );

    /**
     * If the external client doesn't send us a list of enabled features then we ignore
     * and don't initialize the bridge mapping.
     */
    if (!externalFeatures) {
      // Fallback: handshake did not return features; continue in embed mode

      onFallback();

      return;
    }

    /**
     * The local and external features list is merged to determine what features they both support.
     */
    const mergedFeatureList = mergeEnabledFeatures(localFeatureList, externalFeatures);
    const clients = {
      localClientId: localClient,
      externalClientId: externalClient,
    };

    const cleanupLocalHandlers = mapBridgeCommandsToLocalMethods(bridge, {
      getBridgeData,
      featureList: mergedFeatureList,
      clients,
    });

    sendInitialBridgeCommands(bridge, {
      getBridgeData,
      featureList: mergedFeatureList,
      clients,
    });

    /**
     * Now that the bridge is initialized and we know the client IDs
     * swap out the singleton for the PanelManager to be the native embed panel manager.
     */
    container.registerSingleton(
      'PanelManager',
      () => new NativeEmbedPanelManager(bridge, clients, activityLogger)
    );

    return () => {
      bridge.destroy();
      cleanupLocalHandlers();
    };
  } catch (e) {
    onFallback(e);
  }
}
